import React from "react";
import {
  TrendingUp as TrendingUpIcon,
  FeedbackOutlined as FeedbackIcon,
  ContactMail as ContactMailIcon,
  FitnessCenter as FitnessCenterIcon,
  Groups as GroupsIcon,
  People as PeopleIcon,
  Star as StarIcon
} from "@mui/icons-material";

/**
 * Get column definitions for all dashboard tables
 * @param {Function} t - Translation function
 * @returns {Object} Column definitions for each container type
 */
export const getColumnDefinitions = (t) => ({
  activePrograms: [
    { key: 'name', header: t('dashboard.tableHeaders.activePrograms.program'), dataKey: 'name' },
    { key: 'patient', header: t('dashboard.tableHeaders.activePrograms.patient'), dataKey: 'patient' },
    { key: 'status', header: t('dashboard.tableHeaders.activePrograms.status'), dataKey: 'status' },
    { key: 'progress', header: t('dashboard.tableHeaders.activePrograms.progress'), dataKey: 'progress' }
  ],
  latestFeedback: [
    { key: 'patient', header: t('dashboard.tableHeaders.latestFeedback.patient'), dataKey: 'patient' },
    { key: 'rating', header: t('dashboard.tableHeaders.latestFeedback.rating'), dataKey: 'rating' },
    { key: 'comment', header: t('dashboard.tableHeaders.latestFeedback.comment'), dataKey: 'comment' },
    { key: 'date', header: t('dashboard.tableHeaders.latestFeedback.date'), dataKey: 'date' }
  ],
  programRequests: [
    { key: 'patient', header: t('dashboard.tableHeaders.programRequests.patient'), dataKey: 'patient' },
    { key: 'request', header: t('dashboard.tableHeaders.programRequests.request'), dataKey: 'request' },
    { key: 'date', header: t('dashboard.tableHeaders.programRequests.date'), dataKey: 'date' },
    { key: 'status', header: t('dashboard.tableHeaders.programRequests.status'), dataKey: 'status' }
  ],
  latestPatients: [
    { key: 'name', header: t('dashboard.tableHeaders.latestPatients.name'), dataKey: 'name' },
    { key: 'diagnosis', header: t('dashboard.tableHeaders.latestPatients.diagnosis'), dataKey: 'diagnosis' },
    { key: 'active_program_name', header: t('dashboard.tableHeaders.latestPatients.activeProgram'), dataKey: 'active_program_name' },
    { key: 'created_date', header: t('dashboard.tableHeaders.latestPatients.created'), dataKey: 'created_date' }
  ],
  latestOwnExercises: [
    { key: 'name', header: t('dashboard.tableHeaders.latestOwnExercises.exercise'), dataKey: 'name' },
    { key: 'created', header: t('dashboard.tableHeaders.latestOwnExercises.created'), dataKey: 'created' },
    { key: 'category', header: t('dashboard.tableHeaders.latestOwnExercises.category'), dataKey: 'category' },
    { key: 'difficulty', header: t('dashboard.tableHeaders.latestOwnExercises.difficulty'), dataKey: 'difficulty' }
  ],
  latestCommunityExercises: [
    { key: 'name', header: t('dashboard.tableHeaders.latestCommunityExercises.exercise'), dataKey: 'name' },
    { key: 'author', header: t('dashboard.tableHeaders.latestCommunityExercises.author'), dataKey: 'author' },
    { key: 'category', header: t('dashboard.tableHeaders.latestCommunityExercises.category'), dataKey: 'category' },
    { key: 'created_date', header: t('dashboard.tableHeaders.latestCommunityExercises.created'), dataKey: 'created_date' }
  ],
  pendingReviews: [
    { key: 'exercise_name', header: t('dashboard.tableHeaders.pendingReviews.exercise'), dataKey: 'exercise_name' },
    { key: 'created_date', header: t('dashboard.tableHeaders.pendingReviews.created'), dataKey: 'created_date' },
    { key: 'review_status', header: t('dashboard.tableHeaders.pendingReviews.status'), dataKey: 'review_status', type: 'reviewStatus' },
    { key: 'payment_status', header: t('dashboard.tableHeaders.pendingReviews.payment'), dataKey: 'payment_status', type: 'paymentStatus' }
  ]
});

/**
 * Get container configurations for the dashboard
 * @param {Function} t - Translation function
 * @param {Function} renderGenericTable - Function to render tables
 * @param {Object} dashboardData - Current dashboard data
 * @param {boolean} isLoading - Loading state
 * @param {Object} columnDefinitions - Column definitions from getColumnDefinitions
 * @returns {Array} Container configuration array
 */
export const getContainers = (t, renderGenericTable, dashboardData, isLoading, columnDefinitions) => [
  {
    key: 'activePrograms',
    title: t('dashboard.containers.activePrograms.title'),
    subtitle: t('dashboard.containers.activePrograms.subtitle'),
    icon: <TrendingUpIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.activePrograms, isLoading, columnDefinitions.activePrograms)
  },
  {
    key: 'latestFeedback',
    title: t('dashboard.containers.latestFeedback.title'),
    subtitle: t('dashboard.containers.latestFeedback.subtitle'),
    icon: <FeedbackIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.latestFeedback, isLoading, columnDefinitions.latestFeedback)
  },
  {
    key: 'latestPatients',
    title: t('dashboard.containers.latestPatients.title'),
    subtitle: t('dashboard.containers.latestPatients.subtitle'),
    icon: <PeopleIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.latestPatients, isLoading, columnDefinitions.latestPatients)
  },
  {
    key: 'programRequests',
    title: t('dashboard.containers.programRequests.title'),
    subtitle: t('dashboard.containers.programRequests.subtitle'),
    icon: <ContactMailIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.programRequests, isLoading, columnDefinitions.programRequests)
  },
  {
    key: 'latestCommunityExercises',
    title: t('dashboard.containers.latestCommunityExercises.title'),
    subtitle: t('dashboard.containers.latestCommunityExercises.subtitle'),
    icon: <GroupsIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.latestCommunityExercises, isLoading, columnDefinitions.latestCommunityExercises)
  },
  {
    key: 'latestOwnExercises',
    title: t('dashboard.containers.latestOwnExercises.title'),
    subtitle: t('dashboard.containers.latestOwnExercises.subtitle'),
    icon: <FitnessCenterIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.latestOwnExercises, isLoading, columnDefinitions.latestOwnExercises)
  },
  {
    key: 'pendingReviews',
    title: t('dashboard.containers.pendingReviews.title'),
    subtitle: t('dashboard.containers.pendingReviews.subtitle'),
    icon: <StarIcon />,
    hasAddButton: false,
    renderContent: () => renderGenericTable(dashboardData.pendingReviews || [], isLoading, columnDefinitions.pendingReviews)
  }
];



